// Robot Dual-SN validation primitives (plan §4.BR-SN-01 / §06 module-manufacturing §2.1 /
// §02 data-model §4.4 / QC-SCH-03..05 / DQ-2..3).
//
// Two orthogonal regexes:
//
//   SERIAL_SHORT_RE        — accepted by the DB CHECK constraint
//                            (legacy "미상" is grandfathered so the
//                            initial import doesn't reject 276 rows)
//
//   SERIAL_SHORT_NEW_RE    — accepted for NEW input (DTO regex)
//                            ("미상" literal is BLOCKED per DQ-2 /
//                            QC-SCH-04 so new writes can't collide
//                            at the @unique index)
//
//   SERIAL_LONG_RE         — full dual-SN longform.
//                            (Prefix | YYWW | ColorMode + serial)
//                            Enumerated values per QC-SCH-05.
//
// The UNKNOWN literal is exported as a named constant so parser
// pre-processors and matchers never drift out of sync.
export const SERIAL_SHORT_UNKNOWN = '미상';
/**
 * Accepted at the DB layer. "미상" kept only so Phase 2's first
 * real-data import can run without blanket-failing every row whose
 * SN column hasn't been filled in yet.
 */
export const SERIAL_SHORT_RE = /^(미상|\d+(-\d+)?)$/;
/**
 * Accepted for NEW writes — blocks "미상" so the `@unique` index
 * doesn't see a duplicate literal across hundreds of rows
 * (DQ-2 / QC-SCH-04). Parsers must substitute null before insert.
 */
export const SERIAL_SHORT_NEW_RE = /^\d+(-\d+)?$/;
/**
 * Longform SN. Two shapes (S2 §1.1):
 *
 *   완성형(컬러):  {Prefix}-{YYWW}-{ColorMode}{Serial}[-{rev}]   예: GEN2-2613-WF207
 *     Prefix     ∈ { D0, D1, D2, GEN2, PRF, Zep, HL }
 *     ColorMode  ∈ { WN, WF, GF, GN, SF, SN, S2 }
 *
 *   센서리스:      {Prefix}-{YYWW}-{Serial}[-{rev}]               예: SEN-2516-090
 *     Prefix     ∈ { SEN }   (색상·불감지 칸 생략)
 *
 *   YYWW       — 4-digit year+week
 *   Serial     — 1+ digits (PC 모듈 번호)
 *   RevSuffix  — optional `-<digits>` (재빌드/in-place 교체 추적).
 *
 * SEN(Sensorless)과 HL(Headless)은 **다른 제품**(사용자 확인). HL(헤드리스)은
 * 색상 White·방향 Left/Right 를 갖는 완성형이며 실데이터도 ColorMode 포함
 * (예: `HL-2612-WF9005`, docs/research/grillx-asset-ledger §). 따라서 HL 은
 * ColorMode 분기에 둔다. 센서리스(ColorMode 없음)는 SEN 만. PRF(Gen1 Profile) 추가.
 * `S2` ColorMode 는 Zephyr `Zep-YYWW-S21NN` 와 함께 도입.
 *
 * 주: S2 §1.2 의 "SEN/HL 동일 센서리스" 기술은 실데이터(HL-2612-WF9005)와 어긋나
 *     폐기됨 — HL 은 완성형(ColorMode) 으로 처리.
 */
export const SERIAL_LONG_RE = /^((D0|D1|D2|GEN2|PRF|Zep|HL)-\d{4}-(WN|WF|GF|GN|SF|SN|S2)\d+|SEN-\d{4}-\d+)(-\d+)?$/;
/** Type guard — matches `SERIAL_SHORT_RE` (including "미상"). */
export function isValidSerialShort(value) {
    return SERIAL_SHORT_RE.test(value);
}
/** Type guard — matches `SERIAL_SHORT_NEW_RE` (blocks "미상"). */
export function isAcceptableNewSerialShort(value) {
    return SERIAL_SHORT_NEW_RE.test(value);
}
/** Type guard — matches `SERIAL_LONG_RE`. */
export function isValidSerialLong(value) {
    return SERIAL_LONG_RE.test(value);
}
/**
 * DQ-2 normaliser. Given a raw short-SN cell from the asset ledger,
 * returns the value to store (null for the "미상" literal, trimmed
 * otherwise, null for an empty cell).
 *
 * Parsers should call this before any uniqueness / validation check
 * so the same input → storage mapping is applied everywhere.
 */
export function normaliseSerialShortForImport(raw) {
    if (raw == null)
        return null;
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === SERIAL_SHORT_UNKNOWN)
        return null;
    return trimmed;
}
// ============================================================
// S2 Phase 2.2 — 자동 Long-SN Composer (출고대장 "생산주" 자동 산출)
// ============================================================
//
// `productionWeekFromDate` reproduces the 출고대장 시트2 J열 "생산주"
// formula (ISO 8601 week number) so an operator picks a 생산일자 and
// the YYWW token is derived deterministically. `composeSerialLong`
// then assembles the full Long SN from parts so the assembly floor
// never hand-types the raw string (and the result always matches
// `SERIAL_LONG_RE`).
/**
 * 생산 일자 → YYWW (ISO 8601 week). 출고대장 시트2 J열 "생산주" 자동 산출 재현.
 *
 * 목요일(ISO week 의 기준일)을 기준으로 주차를 계산하므로 연말/연초
 * 경계에서도 표준 ISO week-numbering year 와 일치한다 (예: 1월 1일이
 * 전년도 마지막 주에 속하면 전년도 YY 가 반환됨).
 */
export function productionWeekFromDate(d) {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0
    date.setUTCDate(date.getUTCDate() - dayNum + 3); // 목요일 기준
    const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
    const week = 1 +
        Math.round(((date.getTime() - firstThursday.getTime()) / 86400000 -
            3 +
            ((firstThursday.getUTCDay() + 6) % 7)) /
            7);
    const yy = String(date.getUTCFullYear()).slice(-2);
    return `${yy}${String(week).padStart(2, '0')}`; // "2613"
}
/**
 * 부품 조합 → Long SN 문자열. 결과는 항상 `SERIAL_LONG_RE` 를 만족하도록
 * 설계 (단, 호출자가 유효한 prefix/주차/serial 을 넘긴 경우).
 *
 *   완성형(컬러):  `${modelPrefix}-${productionWeek}-${ColorMode}${pcModule}[-rev]`
 *     ColorMode = 색상 첫 글자(W/G/S) + sensorType(F/N), 예: "WF"
 *   고정 ColorMode(센서리스, FB2 §5B): `${modelPrefix}-${productionWeek}-${fixedColorMode}${pcModule}[-rev]`
 *     fixedColorMode 가 주어지면 색상+불감지 대신 그 토큰을 ColorMode 로 직접 사용.
 *     예: Zephyr `Zep-2617-S2113` (fixedColorMode='S2', pcModule='113').
 *   센서리스(SEN/HL): `${modelPrefix}-${productionWeek}-${pcModule}[-rev]`
 *     (sensorless=true → ColorMode 생략)
 *
 * rev 는 재작업 회차로, 2 이상일 때만 `-N` 접미사를 붙인다 (최초 빌드 = 무접미).
 *
 * 우선순위: fixedColorMode > sensorless > 색상+불감지.
 */
export function composeSerialLong(input) {
    let colorMode;
    if (input.fixedColorMode) {
        // FB2 §5B — 고정 ColorMode(예: 'S2'). 색상·불감지 무시하고 토큰을 그대로 사용.
        colorMode = input.fixedColorMode;
    }
    else if (input.sensorless) {
        // SEN/HL 센서리스 — ColorMode 생략.
        colorMode = '';
    }
    else {
        // 일반 완성형 — 색상 첫 글자 + 불감지.
        const colorInitial = input.color?.[0]?.toUpperCase() ?? '';
        colorMode = `${colorInitial}${input.sensorType ?? ''}`;
    }
    const rev = input.rev && input.rev > 1 ? `-${input.rev}` : '';
    return `${input.modelPrefix}-${input.productionWeek}-${colorMode}${input.pcModule}${rev}`;
}
/**
 * round5 #3b — Long SN 에서 Short SN 파생.
 *
 * 현장 규칙 "Short SN = PC 모듈 번호 (Zephyr 만 '2' 접두)" 는 Long SN 구조에
 * 이미 내재한다: 마지막 세그먼트에서 선행 알파벳(ColorMode 토큰의 문자부)을
 * 벗기면 남는 숫자열이 곧 Short SN 이다 —
 *   GEN2-2613-WF207   → "207"   (= PC 모듈)
 *   Zep-2615-S2110    → "2110"  (fixedColorMode 'S2' 의 숫자 '2' 가 자연 포함)
 *   SEN-2516-090      → "090"   (센서리스 — ColorMode 없음)
 * 재작업 접미(-N)는 제외한다. Long SN 형식이 아니면 null.
 */
export function deriveSerialShortFromLong(serialLong) {
    const m = SERIAL_LONG_RE.exec(serialLong.trim());
    if (!m)
        return null;
    const lastSegment = m[1].split('-')[2] ?? '';
    const digits = lastSegment.replace(/^[A-Za-z]+/, '');
    return digits.length > 0 ? digits : null;
}
//# sourceMappingURL=serial-numbers.js.map